"use client";

import { useMemo, useState } from "react";
import { AlertCircle, Check, ChevronDown, Info, ShieldCheck, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { NoticeDialogProps, NoticeVariant } from "./NoticeTypes";
import { Button } from "@/components/ui/button";

type Layout = "dialog" | "drawer";

type Props = NoticeDialogProps & {
  layout: Layout;
  onClose: () => void;
};

const toneStyles: Record<NoticeVariant, { iconBg: string; iconColor: string; confirmBg: string; confirmHover: string }> = {
  warning: {
    iconBg: "bg-amber-50",
    iconColor: "text-amber-500",
    confirmBg: "bg-amber-500",
    confirmHover: "hover:bg-amber-500/90",
  },
  danger: {
    iconBg: "bg-red-50",
    iconColor: "text-red-500",
    confirmBg: "bg-red-500",
    confirmHover: "hover:bg-red-500/90",
  },
  success: {
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-500",
    confirmBg: "bg-emerald-500",
    confirmHover: "hover:bg-emerald-500/90",
  },
  info: {
    iconBg: "bg-sky-50",
    iconColor: "text-sky-500",
    confirmBg: "bg-sky-500",
    confirmHover: "hover:bg-sky-500/90",
  },
  permission: {
    iconBg: "bg-indigo-50",
    iconColor: "text-indigo-500",
    confirmBg: "bg-indigo-500",
    confirmHover: "hover:bg-indigo-500/90",
  },
};

function iconByVariant(variant: NoticeVariant) {
  if (variant === "danger") return <Trash2 className="h-7 w-7" />;
  if (variant === "success") return <Check className="h-7 w-7" />;
  if (variant === "info") return <Info className="h-7 w-7" />;
  if (variant === "permission") return <ShieldCheck className="h-7 w-7" />;
  return <AlertCircle className="h-7 w-7" />;
}

export default function NoticeContent({ error, onConfirm, onClose, layout, variant = "warning" }: Props) {
  const { t } = useTranslation();
  const styles = toneStyles[variant];
  const hasFieldErrors = !!error?.errors && Object.keys(error.errors).length > 0;
  const [showDetails, setShowDetails] = useState(true);

  const normalizedFieldErrors = useMemo(() => {
    if (!error?.errors) return [];
    return Object.entries(error.errors).map(([field, messages]) => {
      const uniqueMessages = Array.from(new Set((messages || []).filter(Boolean)));
      const prettifiedLabel = t(`Notice.fields.${field}`, {
        defaultValue: field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      });
      return { field, label: prettifiedLabel, messages: uniqueMessages };
    });
  }, [error?.errors, t]);

  const description =
    variant === "success"
      ? t("Notice.description.success", { defaultValue: "La acción se realizó correctamente." })
      : error?.message?.trim() ||
        t("Notice.description.default", {
          defaultValue: "Ocurrió un problema al procesar tu solicitud. Revisa los detalles y vuelve a intentarlo.",
        });

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    onClose();
  };

  return (
    <div className={layout === "dialog" ? "px-0" : "px-1 pb-2"}>
      {layout === "dialog" && (
        <button
          className="absolute right-5 top-5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
          aria-label="Cerrar"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>
      )}

      <div className="flex justify-center pb-2">
        <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${styles.iconBg} ${styles.iconColor}`}>
          {iconByVariant(variant)}
        </div>
      </div>

      <div className="items-center space-y-2 text-center">
        <p className="text-xl font-semibold tracking-tight text-slate-900 mb-0">
          {t(`Notice.title.${variant}`, {
            defaultValue:
              variant === "success"
                ? "Success"
                : variant === "danger"
                  ? "Delete"
                  : variant === "info"
                    ? "Information"
                    : variant === "permission"
                      ? "Permissions"
                      : "No pudimos procesar tu solicitud",
          })}
        </p>
        {(error?.endpointName || typeof error?.status === "number") && (
          <div className="flex items-center justify-center gap-2 text-[11px] text-slate-500 mt-1">
            {error?.endpointName && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                <span className="font-semibold">
                  {t("Notice.meta.endpoint", { defaultValue: "endpoint:" })}
                </span>
                <span className="text-slate-700">{error.endpointName}</span>
              </span>
            )}
            {typeof error?.status === "number" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1">
                <span className="font-semibold">
                  {t("Notice.meta.status", { defaultValue: "status:" })}
                </span>
                <span className="text-slate-700">{error.status}</span>
              </span>
            )}
          </div>
        )}
        {!hasFieldErrors && (
          <p className="text-muted-foreground">{description}</p>
        )}
      </div>

      {hasFieldErrors && variant !== "success" && (
        <div className="mt-4 w-full text-sm text-slate-600">
          <div className="border-t border-slate-100 pt-3">
            <div className="mb-2 flex cursor-pointer items-center justify-between" onClick={() => setShowDetails((prev) => !prev)}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {description}
              </p>
              <div className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700">
                <span>
                  {showDetails
                    ? t("Notice.meta.hide", { defaultValue: "Ocultar" })
                    : t("Notice.meta.show", { defaultValue: "Ver" })}
                </span>
                <ChevronDown className={`h-3 w-3 transition-transform ${showDetails ? "rotate-180" : ""}`} />
              </div>
            </div>

            {showDetails && (
              <div className="mt-3 max-h-48 w-full overflow-auto rounded-sm bg-slate-900/95 px-3 py-2 text-[11px] text-slate-100">
                <ul className="space-y-2">
                  {normalizedFieldErrors.map(({ field, label, messages }) => (
                    <li key={field} className="border-b border-slate-700 pb-2 last:border-0 last:pb-0">
                      <p className="text-[11px] font-semibold tracking-wide text-slate-200">{label}</p>
                      <ul className="mt-1 space-y-1 text-[13px] text-slate-100">
                        {messages.map((msg) => (
                          <li key={`${field}-${msg}`} className="leading-snug">• {msg}</li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 flex w-full flex-row gap-3 sm:justify-center">
        {variant === "success" && (
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-2xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 shadow-none transition hover:bg-slate-200"
          >
            {t("Notice.action.cancel", { defaultValue: "Cancel" })}
          </button>
        )}

        <Button
          type="button"
          onClick={handleConfirm}
          className={`flex-1 h-11 text-base font-bold text-white rounded-sm shadow-none transition ${styles.confirmBg} ${styles.confirmHover}`}
        >
          {t("Notice.action.confirm", { defaultValue: "Entendido" })}
        </Button>
      </div>
    </div>
  );
}
